import PropTypes from 'prop-types';
import styled from 'styled-components';

import { IconButton } from '@mui/material';
import Grid from 'material/components/Grid';
import DeleteIcon from '@mui/icons-material/Delete';

import { LandingPageInput, LandingPageSelect } from 'common/components/LandingPageInputs';

import { colors, fieldTypes } from 'utils/constants';
import { SendMessageButton } from 'globalStyles';
import { customFieldType } from 'common/typings';

const ContactWrapper = styled.div`
  border: 2px solid ${colors.wildSand};
  padding: 3px;
`;

const FieldsWrapper = styled.div`
  display: grid;
  gap: 0 16px;
  grid-template-columns: 1fr 1fr 1fr;
  padding-bottom: 32px;
`;

const SendMessageButtonWrapper = styled(Grid)`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

const RemoveFieldWrapper = styled.div`
  position: relative;
`;

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: -22px;
  z-index: 1;
  color: #888;
`;

/**
 * Non-interactive preview of a landing page contact form: the standard
 * contact fields plus any custom fields (each removable via a delete icon)
 * and the styled submit button. Use in the action center and signup creation
 * flows to show the form as it will appear on the landing page.
 */
const PreviewLandingPageFields = ({
  buttonColor,
  buttonName,
  customFields,
  handleRemoveCustomField,
  textColor,
}) => (
  <ContactWrapper>
    <FieldsWrapper>
      <LandingPageSelect name="Prefix" />
      <LandingPageInput name="First Name" />
      <LandingPageInput name="Last Name" />
      <LandingPageInput name="Street Address" />
      <LandingPageInput name="Zip Code" />
      <LandingPageInput name="Email" />
      <LandingPageInput name="Phone Number" />
      {!!customFields?.length &&
        customFields.map(customField =>
          customField.fieldType === fieldTypes.singleChoiceField ? (
            <RemoveFieldWrapper key={customField.label}>
              <LandingPageSelect name={customField.label} />
              <RemoveButton
                aria-label="remove field"
                onClick={() => handleRemoveCustomField(customField.uuid)}
              >
                <DeleteIcon />
              </RemoveButton>
            </RemoveFieldWrapper>
          ) : (
            <RemoveFieldWrapper key={customField.label}>
              <LandingPageInput name={customField.label} />
              <RemoveButton
                aria-label="remove field"
                onClick={() => handleRemoveCustomField(customField.uuid)}
              >
                <DeleteIcon />
              </RemoveButton>
            </RemoveFieldWrapper>
          )
        )}
    </FieldsWrapper>
    <SendMessageButtonWrapper size={12}>
      <SendMessageButton buttoncolor={buttonColor} textcolor={textColor}>
        {buttonName}
      </SendMessageButton>
    </SendMessageButtonWrapper>
  </ContactWrapper>
);

PreviewLandingPageFields.defaultProps = {
  buttonName: 'Send Message',
};

PreviewLandingPageFields.propTypes = {
  /** Background color of the previewed submit button. */
  buttonColor: PropTypes.string.isRequired,
  /** Label of the previewed submit button. */
  buttonName: PropTypes.string,
  customFields: PropTypes.arrayOf(customFieldType).isRequired,
  /** Called with the custom field's uuid when its delete icon is clicked. */
  handleRemoveCustomField: PropTypes.func.isRequired,
  /** Text color of the previewed submit button. */
  textColor: PropTypes.string.isRequired,
};

export default PreviewLandingPageFields;
